import { CommonModule } from '@angular/common';
import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ChatService } from '../chat.service';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faDownload, faPaperPlane, faTrash } from '@fortawesome/pro-solid-svg-icons';

@Component({
  selector: 'app-chat-window',
  templateUrl: './chat-window.component.html',
  styleUrls: ['./chat-window.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, FaIconComponent],
})
export class ChatWindowComponent implements OnInit, OnDestroy {
  @Output() closeChat = new EventEmitter<void>();
  @ViewChild('chatBody') private chatBody!: ElementRef;

  messages: Array<{ user: string; message: string }> = [];
  newMessage = '';
  faPaperPlane = faPaperPlane;
  faTrash = faTrash;
  faDownload = faDownload;
  isResponding = false;
  currentMessage = '';
  private subscriptions = new Subscription();
  private scrollTimeoutId: number | null = null;

  constructor(
    private chatService: ChatService,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnInit(): void {
    this.chatService.startConnection();
    this.subscriptions.add(
      this.chatService.message$.subscribe(message => {
        this.messages.push(message);
        this.scheduleScrollToBottom();
      }),
    );
    this.subscriptions.add(
      this.chatService.currentMessage$.subscribe(chunk => {
        this.currentMessage += chunk;
        this.scheduleScrollToBottom();
      }),
    );
    this.subscriptions.add(
      this.chatService.isResponding$.subscribe(isResponding => {
        this.isResponding = isResponding;
        if (!isResponding) {
          this.currentMessage = '';
          this.scheduleScrollToBottom();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.scrollTimeoutId !== null) {
      window.clearTimeout(this.scrollTimeoutId);
      this.scrollTimeoutId = null;
    }
  }

  sendMessage(): void {
    if (this.newMessage.trim()) {
      this.chatService.sendMessage('Me', this.newMessage);
      this.newMessage = '';
      this.scheduleScrollToBottom();
    }
  }

  clearChat(): void {
    this.messages = [];
    this.currentMessage = '';
    this.isResponding = false;
    this.chatService.clearConversation();
    this.scheduleScrollToBottom();
  }

  exportChat(): void {
    const hasContent = this.messages.length > 0 || this.currentMessage.trim().length > 0;
    if (!hasContent) {
      return;
    }

    const lines: string[] = this.messages.map(msg => `${msg.user}: ${this.stripHtml(msg.message)}`);

    if (this.currentMessage.trim()) {
      lines.push(`Bueno (draft): ${this.stripHtml(this.currentMessage)}`);
    }

    const exportContent = lines.join('\n\n');
    const blob = new Blob([exportContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const anchor = this.document.createElement('a');
    anchor.href = url;
    anchor.download = `bueno-chat-${this.getFormattedTimestamp()}.txt`;
    anchor.style.display = 'none';
    this.document.body.appendChild(anchor);
    anchor.click();
    this.document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  private scrollToBottom(): void {
    if (!this.chatBody) {
      return;
    }

    const nativeElement = this.chatBody.nativeElement;
    nativeElement.scrollTop = nativeElement.scrollHeight;
  }

  private scheduleScrollToBottom(): void {
    if (this.scrollTimeoutId !== null) {
      window.clearTimeout(this.scrollTimeoutId);
    }

    this.scrollTimeoutId = window.setTimeout(() => {
      this.scrollTimeoutId = null;
      this.scrollToBottom();
    }, 0);
  }

  private stripHtml(content: string): string {
    const tempElement = this.document.createElement('div');
    tempElement.innerHTML = content;
    return (tempElement.textContent || tempElement.innerText || '').trim();
  }

  private getFormattedTimestamp(): string {
    const now = new Date();
    const pad = (value: number) => value.toString().padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
  }
}
